import { ChangeEvent, forwardRef, ReactNode, RefObject, useCallback } from "react"
import {
    type TextFieldProps,
    styled,
    filledInputClasses,
    colors,
    useTheme,
    Box,
    SxProps,
    Theme,
    autocompleteClasses,
    TextField,
    buttonClasses,
} from "@mui/material"
import { alpha, Stack } from "@mui/system"
import { classes } from "@tm/utils"
import { IconButton, SearchButton } from "../../muiStyled/Inputs/button"
import { Icon } from "../../muiStyled/DataDisplay/Icons"
import { Loader } from "../../muiStyled/Feedback/loader"

export type SearchTextFieldProps = TextFieldProps & {
    alwaysColoredBorder?: boolean
    cutEdge?: boolean
    hideOutline?: boolean
}

const SearchFieldContainer = styled(Box, {
    name: "SearchFieldContainer",
    slot: "root",
    overridesResolver: (_, styles) => styles.root,
})(() => ({
    width: "100%",
    display: "flex",
}))

const SearchTextField = styled(TextField, {
    name: "SearchTextField",
    slot: "root",
    overridesResolver(_, styles) {
        return styles.root
    },
    shouldForwardProp: (prop: string) => !["alwaysColoredBorder", "cutEdge", "hideOutline"].includes(prop),
})<{
    alwaysColoredBorder?: boolean
    cutEdge?: boolean
    hideOutline?: boolean
}>(({ theme, alwaysColoredBorder, hideOutline, size = "medium" }) => {
    const searchFieldHeight = { small: 32, medium: 38, large: 48, extralarge: 48 }
    const searchFieldFontSize = { small: 14, medium: 16, large: 18, extralarge: 18 }
    const fontSize = theme.overwrites?.components?.textfield?.fontSize?.[size] || searchFieldFontSize[size]
    const borderColor = theme.overwrites?.components?.textfield?.border?.color || (alwaysColoredBorder ? theme.palette.highlight.main : undefined)
    const overwriteBackgroundColor = theme.overwrites?.components?.textfield?.backgroundColor
    return {
        [`.${filledInputClasses.root}`]: {
            ...(borderColor &&
                !hideOutline && {
                    border: `1px solid ${borderColor}`,
                }),
            color: theme.colors?.dark || "#000000",
            height: searchFieldHeight[size],
            padding: 0,
            overflow: "hidden",
            borderRadius: theme.radius.default,
            ...(overwriteBackgroundColor && { backgroundColor: overwriteBackgroundColor }),
            transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
            "&:before": {
                content: "none",
            },
            "&:after": {
                content: "none",
            },

            "&:hover": {
                backgroundColor: theme.overwrites?.components?.textfield?.backgroundColor || colors.grey[50],
            },
            [`&.${filledInputClasses.focused}`]: {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.overwrites?.components?.textfield?.backgroundColor || colors.grey[50],
                boxShadow: theme.shadows[4], // `rgba(0, 0, 0, 0.2) 0px 4px 10px 0px`,
            },
            [`&.${filledInputClasses.error}`]: {
                borderColor: theme.palette.error.main,
                borderBottom: `4px solid ${theme.palette.error.main}`,
                marginBottom: "0px",
                [`&.${filledInputClasses.focused}`]: {
                    boxShadow: `${alpha(theme.palette.error.main, 0.25)} 0 0 0 2px`,
                },
            },
        },
        [`.${filledInputClasses.input}`]: {
            padding: 8,
            boxSizing: "content-box",
            fontSize,
            lineHeight: fontSize,
            height: fontSize,
        },
        [`.${autocompleteClasses.expanded} &.${autocompleteClasses.inputRoot}`]: {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        },
        ".has-suggestions": {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        },
    }
})

export const InlineSearchButton = styled(SearchButton, {
    name: "InlineSearchButton",
    slot: "root",
    overridesResolver(_, styles) {
        return styles.root
    },
    shouldForwardProp: (prop: string) => prop !== "cutEdge",
})<{ cutEdge?: boolean }>(({ theme }) => {
    return {
        [`&.${buttonClasses.contained}`]: {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
        },
        marginLeft: 0,
        [`.${buttonClasses.sizeExtraLarge}`]: {
            padding: 12,
        },
        [`.${buttonClasses.sizeLarge}`]: {
            padding: 10,
        },
        [`.${buttonClasses.sizeMedium}`]: {
            padding: 8,
        },
        [`.${buttonClasses.sizeSmall}`]: {
            padding: 6,
        },
    }
})

const CutEdgeStack = styled(Stack, { shouldForwardProp: (prop: string) => !["cutEdge"].includes(prop) })<{ cutEdge?: boolean }>(
    ({ cutEdge, theme }) => ({
        ...(cutEdge && {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,

            [`&::after`]: {
                content: "''",
                position: "absolute",
                width: 0,
                height: 0,
                border: "7px solid transparent",
                right: "-1px",
                bottom: "-1px",
                borderColor: "transparent #fff #fff transparent",
                transition: theme.transitions.create(["border-color"]),
            },
            [`&::before`]: {
                content: "''",
                position: "absolute",
                right: 0,
                bottom: 0,
                width: 0,
                height: 0,
                borderStyle: "solid",
                borderWidth: "7px",
                borderColor: `transparent  ${theme.overwrites?.components?.textfield?.border?.color}  ${theme.overwrites?.components?.textfield?.border?.color} transparent`,
                background: theme.overwrites?.components?.textfield?.backgroundColor,
                transition: theme.transitions.create(["border-color"]),
            },
            [`.${filledInputClasses.focused} &::before`]: {
                borderColor: `transparent ${theme.palette.primary.main} ${theme.palette.primary.main} transparent`,
            },
        }),
    })
)

const CenteredAdornment = styled(Box, {
    shouldForwardProp: (props: string) => !["cutEdge"].includes(props),
})(({ cutEdge }: { cutEdge?: boolean }) => {
    return {
        display: "flex",
        height: "100%",
        alignItems: "center",
        marginRight: !cutEdge ? "8px" : "16px",
        "&:empty": {
            marginRight: !cutEdge ? 0 : "8px",
        },
    }
})

export type SearchFieldProps = Omit<TextFieldProps, "value" | "onChange" | "color"> & {
    value: string
    isLoading?: boolean
    onChange(value: string): void
    onStartSearch(): void
    onClear?(): void
    alwaysColoredBorder?: boolean
    color?: "primary" | "secondary" | "error" | "info" | "success" | "warning" | "highlight"
    buttonDisabled?: boolean
    buttonTitle?: string
    hideClearButton?: boolean
    tooltip?: string
    wrapperBoxSx?: SxProps<Theme>
    inputRef?: RefObject<HTMLInputElement>
    inputEndAdornment?: ReactNode
    hideOutline?: boolean
    hasSuggestions?: boolean
}

export const SearchField = forwardRef<HTMLDivElement, SearchFieldProps>((props, ref) => {
    const theme = useTheme()
    const cutEdge = theme?.overwrites?.components?.textfield?.cutEdge
    const {
        onStartSearch,
        onChange,
        onKeyUp,
        onClear,
        isLoading,
        color,
        buttonDisabled,
        size = "medium",
        buttonTitle,
        hideClearButton,
        tooltip,
        children,
        wrapperBoxSx,
        inputRef,
        hasSuggestions,
        inputEndAdornment,
        ...rest
    } = props

    const handleClickClear = useCallback(() => {
        onChange("")
        onClear?.()
        inputRef?.current?.focus()
    }, [onChange, onClear])

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            onChange(e.currentTarget.value)
        },
        [onChange]
    )

    const handleKeyStroke = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Enter") {
                onStartSearch()
            }
            onKeyUp?.(e)
        },
        [onStartSearch, onKeyUp]
    )

    const handleClickSearch = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            onStartSearch()
            e.stopPropagation()
        },
        [onStartSearch]
    )

    function renderSearchButton() {
        return (
            <InlineSearchButton
                title={buttonTitle}
                color={color}
                onClick={handleClickSearch}
                cutEdge={cutEdge}
                disabled={buttonDisabled}
                size={size}
            />
        )
    }

    return (
        <Box position="relative" sx={wrapperBoxSx}>
            <SearchFieldContainer ref={ref}>
                <SearchTextField
                    autoComplete="off"
                    cutEdge={cutEdge}
                    size={size}
                    {...rest}
                    inputRef={inputRef}
                    onChange={handleChange}
                    onKeyUp={handleKeyStroke}
                    variant="filled"
                    // eslint-disable-next-line react/jsx-no-duplicate-props

                    InputProps={{
                        ...rest.InputProps,
                        className: classes(rest.InputProps?.className, hasSuggestions && "has-suggestions"),
                        endAdornment: (
                            <>
                                {cutEdge && <CutEdgeStack paddingRight="40px" cutEdge={cutEdge} />}
                                <CenteredAdornment cutEdge={cutEdge}>
                                    {isLoading && <Loader size="small" marginRight={0.5} />}
                                    {!!props.value && hideClearButton !== false && (
                                        <IconButton size={size || "small"} onClick={handleClickClear} sx={{ margin: 0 }}>
                                            <Icon name="close" />
                                        </IconButton>
                                    )}
                                    {inputEndAdornment}
                                </CenteredAdornment>
                                {!cutEdge && renderSearchButton()}
                            </>
                        ),
                    }}
                />
                {cutEdge && renderSearchButton()}
            </SearchFieldContainer>
            {children}
            {tooltip && (
                <Box className="search-container__hint" style={{ display: "flex", alignItems: "flex-start", paddingTop: "0.5em" }}>
                    <Icon name="search" />
                    <Box className="text text--s" style={{ paddingLeft: "0.5em" }}>
                        {tooltip}
                    </Box>
                </Box>
            )}
        </Box>
    )
})

// export const SearchField = forwardRef(SearchFieldComponent)
